// Renders a single slide inside a 16:9 container.
// Uses the deck theme colors so every slide follows the brand automatically.
// Includes a subtle "Presented by LAYOR Digital Group" footer on every slide.
export default function SlideView({ slide, theme, showFooter = true }) {
  if (!slide) return null;
  const bg = slide.background_color || theme?.background_color || "#F6F5F0";
  const primary = theme?.primary_color || "#0A152C";
  const accent = theme?.accent_color || "#C6A85A";
  const text = theme?.text_color || primary;
  const pink = "#E33278";

  const wrap = { backgroundColor: bg, color: text };
  const titleStyle = { color: primary };
  const accentStyle = { color: accent };

  const isDarkLayout = slide.layout === "brand_cover" || slide.layout === "brand_closing";

  return (
    <div className="w-full h-full relative overflow-hidden" style={wrap}>
      {/* Top accent bar */}
      <div className="absolute inset-x-0 top-0 h-1" style={{ backgroundColor: accent }} />

      {/* --- BRAND COVER (dark, gold L-mark, gold+pink accents) --- */}
      {slide.layout === "brand_cover" && (
        <div className="absolute inset-0" style={{ backgroundColor: "#0A152C", color: "#FFFFFF" }}>
          {/* subtle gradient wash */}
          <div className="absolute inset-0" style={{ background: "radial-gradient(circle at 85% 15%, rgba(198,168,90,0.18), transparent 55%), radial-gradient(circle at 15% 85%, rgba(227,50,120,0.14), transparent 55%)" }} />
          <div className="absolute inset-0 p-[4%] grid grid-cols-12 gap-6 items-center">
            <div className="col-span-4 flex items-center justify-center">
              <img src="/brand/layor-icon.png" alt="LAYOR" className="w-[70%] max-w-[220px] object-contain drop-shadow-[0_10px_30px_rgba(198,168,90,0.35)]" />
            </div>
            <div className="col-span-8 pl-[3%] border-l" style={{ borderColor: "rgba(198,168,90,0.55)" }}>
              <div className="text-[1vw] uppercase tracking-[0.32em]" style={{ color: "#C6A85A" }}>LAYOR Digital Group</div>
              <div className="mt-[1.5vw] font-display-black text-[5.6vw] leading-[0.98] tracking-tight" style={{ color: "#FFFFFF" }}>
                {slide.title || "Deck Title"}
              </div>
              {slide.subtitle && (
                <div className="mt-[1.4vw] text-[1.5vw] leading-snug" style={{ color: "rgba(255,255,255,0.82)" }}>{slide.subtitle}</div>
              )}
              <div className="mt-[2.4vw] flex flex-wrap items-center gap-x-[1vw] gap-y-1 text-[0.85vw] uppercase tracking-[0.32em] font-semibold" style={{ color: "#C6A85A" }}>
                <span>Leadership</span><span>·</span><span>Strategy</span><span>·</span><span>Technology</span><span>·</span>
                <span style={{ color: pink }}>Impact</span>
              </div>
            </div>
          </div>
          {/* Gold + pink rule at bottom */}
          <div className="absolute bottom-0 left-0 right-0 h-[3px] flex">
            <div className="flex-[3]" style={{ backgroundColor: "#C6A85A" }} />
            <div className="flex-1" style={{ backgroundColor: pink }} />
          </div>
        </div>
      )}

      {/* --- BRAND CLOSING (dark, aperture ring, contact strip) --- */}
      {slide.layout === "brand_closing" && (
        <div className="absolute inset-0" style={{ backgroundColor: "#0B0B0F", color: "#FFFFFF" }}>
          <div className="absolute inset-0" style={{ background: "radial-gradient(circle at 30% 40%, rgba(198,168,90,0.22), transparent 60%)" }} />
          <div className="absolute inset-0 p-[5%] flex flex-col justify-center">
            <div className="flex items-center gap-[1vw]">
              <div className="w-[3vw] h-[3vw] rounded-full" style={{ border: "0.2vw solid #C6A85A", boxShadow: "inset 0 0 0 0.35vw #0B0B0F, inset 0 0 0 0.55vw #E33278" }} />
              <div className="text-[1vw] uppercase tracking-[0.32em]" style={{ color: "#C6A85A" }}>Thank You</div>
            </div>
            <div className="mt-[1.6vw] font-display-black text-[6.4vw] leading-[0.98]" style={{ color: "#FFFFFF" }}>
              {slide.title || "Thank You"}
            </div>
            {slide.subtitle && (
              <div className="mt-[1.4vw] text-[1.4vw] leading-snug" style={{ color: "rgba(255,255,255,0.78)" }}>{slide.subtitle}</div>
            )}
            <div className="mt-[2.4vw] flex items-center gap-[0.8vw]">
              <img src="/brand/layor-icon.png" alt="LAYOR" className="h-[2.6vw] object-contain" />
              <div className="text-[0.95vw] uppercase tracking-[0.32em]" style={{ color: "rgba(255,255,255,0.7)" }}>LAYOR Digital Group</div>
            </div>
          </div>
          <div className="absolute bottom-0 left-0 right-0 h-[3px] flex">
            <div className="flex-1" style={{ backgroundColor: pink }} />
            <div className="flex-[3]" style={{ backgroundColor: "#C6A85A" }} />
          </div>
        </div>
      )}

      {/* --- Standard editable layouts --- */}
      {!isDarkLayout && (
        <div className="absolute inset-0 p-[4%] flex flex-col">
          {slide.layout === "title" && (
            <div className="flex-1 flex flex-col justify-center">
              <div className="text-[1vw] uppercase tracking-[0.3em] mb-4" style={accentStyle}>Presentation</div>
              <div className="font-display-black text-[5vw] leading-[1] tracking-tight" style={titleStyle}>{slide.title || "Untitled"}</div>
              {slide.subtitle && <div className="mt-4 text-[1.6vw] opacity-80">{slide.subtitle}</div>}
            </div>
          )}

          {slide.layout === "bullets" && (
            <>
              <div className="font-display text-[3vw] leading-tight tracking-tight" style={titleStyle}>{slide.title || "Untitled"}</div>
              <ul className="mt-6 space-y-3 text-[1.6vw]">
                {(slide.bullets || []).map((b, i) => (
                  <li key={i} className="flex items-start gap-3">
                    <span className="mt-[0.7vw] h-[0.7vw] w-[0.7vw] rounded-full flex-shrink-0" style={{ backgroundColor: accent }} />
                    <span>{b}</span>
                  </li>
                ))}
              </ul>
            </>
          )}

          {slide.layout === "two_column" && (
            <>
              <div className="font-display text-[3vw] leading-tight tracking-tight" style={titleStyle}>{slide.title || "Untitled"}</div>
              <div className="mt-6 grid grid-cols-2 gap-8 flex-1">
                <div className="text-[1.4vw] leading-relaxed whitespace-pre-wrap">{slide.body}</div>
                <div className="text-[1.4vw] leading-relaxed whitespace-pre-wrap border-l pl-8" style={{ borderColor: accent + "66" }}>{slide.body2}</div>
              </div>
            </>
          )}

          {slide.layout === "image" && (
            <>
              <div className="font-display text-[2.4vw] leading-tight tracking-tight" style={titleStyle}>{slide.title || "Untitled"}</div>
              <div className="mt-3 flex-1 rounded-xl overflow-hidden border" style={{ borderColor: accent + "55" }}>
                {slide.image_url ? (
                  <img src={slide.image_url} alt={slide.caption || slide.title || ""} className="w-full h-full object-cover" />
                ) : (
                  <div className="w-full h-full flex items-center justify-center text-[1.1vw] opacity-60">Add an image URL to display it here</div>
                )}
              </div>
              {slide.caption && <div className="mt-2 text-[1.1vw] opacity-70">{slide.caption}</div>}
            </>
          )}

          {slide.layout === "quote" && (
            <div className="flex-1 flex flex-col justify-center">
              <div className="text-[6vw] leading-none font-display-black" style={accentStyle}>“</div>
              <div className="font-display text-[2.6vw] leading-tight -mt-4">{slide.body || "Add your quote"}</div>
              {slide.attribution && <div className="mt-6 text-[1.2vw] uppercase tracking-[0.28em]" style={accentStyle}>{slide.attribution}</div>}
            </div>
          )}

          {slide.layout === "closing" && (
            <div className="flex-1 flex flex-col justify-center items-start">
              <div className="text-[1vw] uppercase tracking-[0.3em]" style={accentStyle}>Thank you</div>
              <div className="mt-4 font-display-black text-[5.4vw] leading-[1]" style={titleStyle}>{slide.title || "Thank You"}</div>
              {slide.subtitle && <div className="mt-4 text-[1.6vw] opacity-80">{slide.subtitle}</div>}
            </div>
          )}
        </div>
      )}

      {/* Subtle brand footer on every non-brand slide */}
      {showFooter && !isDarkLayout && (
        <div className="absolute bottom-[1.6%] left-[4%] right-[4%] flex items-center justify-between text-[0.8vw]" style={{ color: text, opacity: 0.55 }}>
          <div className="flex items-center gap-[0.5vw]">
            <img src="/brand/layor-icon.png" alt="" className="h-[1.4vw] w-[1.4vw] object-contain" />
            <span className="uppercase tracking-[0.28em]">Presented by LAYOR Digital Group</span>
          </div>
          <div className="h-[1px] w-[8vw]" style={{ backgroundColor: accent, opacity: 0.5 }} />
        </div>
      )}
    </div>
  );
}
